import React, { useEffect, useState } from 'react';
import { Typography, FormControl, CircularProgress, Alert} from '@mui/material';
import { Col, Row } from 'react-bootstrap';
import MultiSelect from '../../elements/MultiSelect';
import MultiSelectDropdown from '../../elements/MultiSelectDropdown';
import { getIssuePriorities } from '../../../modules/jira';
import { useUser } from '../../../context/UserContext';

const TestPlanFilters = ({
  selectedModules,
  setSelectedModules,
  selectedPriority,
  setSelectedPriority,
  currentProject,
  moduleOptions,
}) => {
  const allModuleIds = moduleOptions.map((module) => module.id);
  const linkedJiraProjects = currentProject?.linked_jira_projects || [];
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useUser();
  const userRole = currentUser?.role;
  const isTeamMember = userRole === 'team-member';

  const noModules = moduleOptions?.length === 0;
  const noJira = linkedJiraProjects?.length === 0;
  const canRenderFilters = !noModules && !noJira;
  const allPriorityNames = priorityOptions.map((priority) => priority.name);

  const fetchAllPriorities = async () => {
    setLoading(true);
    try {
      const all = await Promise.all(
        linkedJiraProjects.map((project) =>
          getIssuePriorities(project.linked_project_id, currentProject.id)
        )
      );
      const merged = all.flat().filter(Boolean);
      const unique = [
        ...new Map(merged.map((p) => [p.name, p])).values(),
      ];
      setPriorityOptions(unique);
    } catch (error) {
      console.error('Error fetching priorities:', error);
      setPriorityOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canRenderFilters) {
      fetchAllPriorities();
    }
  }, [linkedJiraProjects, moduleOptions]);

  const handleModuleChange = (value) => {
    setSelectedModules(
      value.includes('all')
        ? selectedModules.length === allModuleIds.length
          ? []
          : allModuleIds
        : value
    );
  };

  const handlePriorityChange = (value) => {
    setSelectedPriority(
      value.includes('all')
        ? selectedPriority.length === allPriorityNames.length
          ? []
          : allPriorityNames
        : value
    );
  };

  const renderAlert = () => {
    if (noModules && noJira) {
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {isTeamMember
            ? 'No modules and no Jira projects are configured. Please ask your admin to add them in the project configurations.'
            : 'No modules and no Jira projects are configured. Please add them in the project configurations.'}
        </Alert>
      );
    }
    if (noModules) {
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {isTeamMember
            ? 'No modules found. Please ask your admin to add them in the project configurations.'
            : 'No modules defined. Please add them in the project configurations.'}
        </Alert>
      );
    }
    if (noJira) {
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {isTeamMember
            ? 'No Jira project linked. Please ask your admin to link one in the project configurations.'
            : 'No Jira project linked. Please link one in the project configurations.'}
        </Alert>
      );
    }
    return null;
  };

  return (
    <div className="p-3 mb-4">
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}
      >
        Test Plan Filters
      </Typography>
      <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
        Select modules and filter test cases based on issue priorities from
        linked Jira projects.
      </Typography>

      {renderAlert()}

      {canRenderFilters && (
        <Row className="g-4">
          <Col md={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle1">Select Modules</Typography>
              <MultiSelectDropdown
                options={moduleOptions}
                selectedValues={selectedModules}
                maxSelections={moduleOptions.length}
                onChange={handleModuleChange}
                placeholder="Select Modules"
                name='modules'
              />
            </FormControl>
          </Col>

          <Col md={6}>
            {loading ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress size={20} />
              </div>
            ) : (
              <FormControl fullWidth>
                <Typography variant="subtitle1">Linked Issue Priorities</Typography>
                <MultiSelect
                  options={allPriorityNames}
                  selectedValues={selectedPriority}
                  maxSelections={allPriorityNames.length}
                  onChange={handlePriorityChange}
                  placeholder="Select Priorities"
                  showSelectAll={true}
                />
              </FormControl>
            )}
          </Col>
        </Row>
      )}
    </div>
  );
};

export default TestPlanFilters;
